// app/context/NotificationsContext.js
import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { io } from 'socket.io-client';
import axios from 'axios';
import { keyframes } from '@emotion/react';
import { getNotifications, markAsRead as markNotificationAsRead } from 'app/helper/ApiUrlHelper';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { TransitionGroup } from 'react-transition-group';
import Collapse from '@mui/material/Collapse';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { useNavigate } from 'react-router-dom';
import AuthContext from './JWTAuthContext';
import { useSnackbar } from './SnackBarContext';

const NotificationsContext = createContext();

const ACTIVE_ALERTS_STORAGE_KEY = 'veekay_active_lead_alerts';
const TRANSIENT_ALERT_DURATION_MS = 7000;

// Flat, calm colors for the lower-urgency (followup/renewal) cards - no animation.
const transientColorMapping = {
    Lead: '#0288d1',
    Opportunity: '#2e7d32',
    Renewal: '#ef6c00',
    Default: '#455a64',
};

// Vibrant, type-specific gradients used for the alert stack cards
const typeGradientMapping = {
    Lead: 'linear-gradient(120deg, #ff416c, #ff4b2b, #ff9a44, #ff416c)',
    new_lead: 'linear-gradient(120deg, #ff416c, #ff4b2b, #ff9a44, #ff416c)',
    Opportunity: 'linear-gradient(120deg, #11998e, #38ef7d, #0575e6, #11998e)',
    followup: 'linear-gradient(120deg, #11998e, #38ef7d, #0575e6, #11998e)',
    Renewal: 'linear-gradient(120deg, #f7971e, #ffd200, #f7971e)',
    renewal: 'linear-gradient(120deg, #f7971e, #ffd200, #f7971e)',
    Default: 'linear-gradient(120deg, #667eea, #764ba2, #6a11cb, #667eea)',
};

const gradientShift = keyframes`
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
`;

const glowPulse = keyframes`
    0%, 100% { box-shadow: 0 0 10px 0 rgba(0,0,0,0.35); }
    50% { box-shadow: 0 0 26px 4px rgba(255,255,255,0.45); }
`;

const ring = keyframes`
    0%, 100% { transform: rotate(0deg); }
    20% { transform: rotate(-15deg); }
    40% { transform: rotate(12deg); }
    60% { transform: rotate(-8deg); }
    80% { transform: rotate(5deg); }
`;

export const NotificationsProvider = ({ children }) => {
    const navigate = useNavigate();
    const { isAuthenticated } = useContext(AuthContext);
    const { showSnackbar } = useSnackbar();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);

    // Stack of unacknowledged lead/business alerts. Persisted so they survive refresh.
    const [activeAlerts, setActiveAlerts] = useState(() => {
        try {
            const stored = localStorage.getItem(ACTIVE_ALERTS_STORAGE_KEY);
            return stored ? JSON.parse(stored) : [];
        } catch (error) {
            return [];
        }
    });

    // Lower-urgency alerts (followup/renewal): shown at the top alongside lead alerts, but
    // calmer and auto-dismissing - not persisted, since they aren't critical enough to
    // survive a refresh or demand acknowledgment.
    const [transientAlerts, setTransientAlerts] = useState([]);
    const transientTimersRef = useRef({});

    const activeAlertsRef = useRef(activeAlerts);
    activeAlertsRef.current = activeAlerts;

    const socketRef = useRef(null);

    const persistActiveAlerts = (alerts) => {
        localStorage.setItem(ACTIVE_ALERTS_STORAGE_KEY, JSON.stringify(alerts));
    };

    const extractLeadIdFromMessage = (message) => {
        const leadIdMatch = message?.match(/ID:?\s*(\d+)/); // Looks for 'ID <number>' or 'ID: <number>' in the message
        return leadIdMatch ? leadIdMatch[1] : null;
    };

    // Alarm audio - loops continuously for as long as there is an unacknowledged
    // NEW LEAD alert. This is intentionally reserved for new leads only: if every
    // notification type demanded acknowledgment, users would reflexively dismiss
    // everything and the signal for a genuinely new lead would get lost.
    const notificationSound = '/sounds/notification.mp3';
    const audioRef = useRef(null);
    if (audioRef.current === null) {
        audioRef.current = new Audio(notificationSound);
        audioRef.current.preload = 'auto';
        audioRef.current.volume = 1.0;
        audioRef.current.loop = true;
    }
    const audio = audioRef.current;

    // One-shot ping for lower-urgency events (followup / renewal) - plays once, no loop.
    const pingAudioRef = useRef(null);
    if (pingAudioRef.current === null) {
        pingAudioRef.current = new Audio(notificationSound);
        pingAudioRef.current.preload = 'auto';
        pingAudioRef.current.volume = 0.6;
    }
    const pingAudio = pingAudioRef.current;

    const playPing = () => {
        pingAudio.currentTime = 0;
        pingAudio.play().catch((error) => {
            console.error('Error playing notification ping:', error);
        });
    };

    useEffect(() => {
        if (activeAlerts.length > 0) {
            audio.play().catch((error) => {
                console.error('Error playing notification alarm:', error);
            });
        } else {
            audio.pause();
            audio.currentTime = 0;
        }
    }, [activeAlerts.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // On a fresh page load (e.g. the tab was closed and reopened, or hard-refreshed),
    // browsers block audio.play() until the user has interacted with the new page at
    // least once - the call above silently fails, so a rehydrated alert shows up but
    // stays mute. Retry once on the first click/keypress/touch so the alarm catches up.
    useEffect(() => {
        const resumeAlarmIfNeeded = () => {
            if (activeAlertsRef.current.length > 0 && audio.paused) {
                audio.play().catch(() => {});
            }
        };
        document.addEventListener('click', resumeAlarmIfNeeded, { once: true });
        document.addEventListener('keydown', resumeAlarmIfNeeded, { once: true });
        document.addEventListener('touchstart', resumeAlarmIfNeeded, { once: true });
        return () => {
            document.removeEventListener('click', resumeAlarmIfNeeded);
            document.removeEventListener('keydown', resumeAlarmIfNeeded);
            document.removeEventListener('touchstart', resumeAlarmIfNeeded);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Shared by pushAlert/pushTransientAlert. `data.id` is deliberately NOT used as a
    // leadId fallback - on 'followup'/'renewal' payloads it's the notification row's own
    // database id, not a lead id, and would silently point View/navigation at the wrong
    // record. Only `new_lead` payloads carry a genuine `leadId` field; everything else
    // falls back to parsing the real id out of the message text.
    const buildAlert = (data, type) => {
        const leadId = data.leadId ?? extractLeadIdFromMessage(data.message);
        return {
            uid: `${type}-${leadId ?? 'na'}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            message: data.message,
            type,
            leadId,
            createdAt: Date.now(),
        };
    };

    // Pushes a persistent, stacked, must-acknowledge alert card. Reserved for new leads.
    const pushAlert = (data, type) => {
        const alert = buildAlert(data, type);

        setActiveAlerts((prev) => {
            const next = [alert, ...prev];
            persistActiveAlerts(next);
            return next;
        });
    };

    const acknowledgeAlert = (uid) => {
        setActiveAlerts((prev) => {
            const next = prev.filter((a) => a.uid !== uid);
            persistActiveAlerts(next);
            return next;
        });
    };

    const handleNavigateAlert = (alert) => {
        if (alert.type === 'Opportunity') {
            navigate('/opportunities', { state: { filterId: alert.leadId } });
        } else if (alert.type === 'Lead' || alert.type === 'new_lead') {
            navigate('/lead', { state: { filterId: alert.leadId } });
        }
        acknowledgeAlert(alert.uid);
    };

    const dismissTransientAlert = (uid) => {
        clearTimeout(transientTimersRef.current[uid]);
        delete transientTimersRef.current[uid];
        setTransientAlerts((prev) => prev.filter((a) => a.uid !== uid));
    };

    // Pushes a calm, auto-dismissing card for lower-urgency events (followup / renewal).
    const pushTransientAlert = (data, type) => {
        const alert = buildAlert(data, type);

        setTransientAlerts((prev) => [alert, ...prev]);
        playPing();

        transientTimersRef.current[alert.uid] = setTimeout(() => {
            dismissTransientAlert(alert.uid);
        }, TRANSIENT_ALERT_DURATION_MS);
    };

    const handleNavigateTransient = (alert) => {
        if (alert.type === 'Opportunity') {
            navigate('/opportunities', { state: { filterId: alert.leadId } });
        } else if (alert.type === 'Lead') {
            navigate('/lead', { state: { filterId: alert.leadId } });
        }
        dismissTransientAlert(alert.uid);
    };

    useEffect(() => {
        const timers = transientTimersRef.current;
        return () => {
            Object.values(timers).forEach(clearTimeout);
        };
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            // Initialize Socket.IO client
            const token = localStorage.getItem('accessToken');
            const roleid = localStorage.getItem('roleId');

            // Fetch initial notifications
            const fetchNotifications = async () => {
                try {
                    const response = await axios({
                        method: 'post',
                        headers: {
                            token: token,
                            roleid: roleid,
                        },
                        url: getNotifications(),
                        data: {},
                    });

                    if (response.data.data) {
                        setNotifications(response.data.data);
                        const count = response.data.data.filter((n) => !n.is_read).length;
                        setUnreadCount(count);
                    } else {
                        console.log('No notifications data received:', response.data.message);
                    }
                } catch (error) {
                    console.error('Error fetching notifications:', error);
                    showSnackbar('Error fetching notifications.', 'error');
                }
            };

            fetchNotifications();

            // Initialize Socket.IO client with updated token
            const socket = io('https://panelapi.veekayassociates.com', {
                auth: { token, roleId: roleid },
                transports: ['websocket'],
            });

            socketRef.current = socket;

            socket.on('connect', () => {
                console.log('Socket.IO connected:', socket.id);
            });

            // Handle incoming 'followup' events - calm auto-dismissing card, no forced acknowledgment
            socket.on('followup', (data) => {
                if (data && data.id) {
                    const type = data.status >= 3 ? 'Opportunity' : 'Lead';
                    setNotifications((prev) => [data, ...prev]);
                    setUnreadCount((prev) => prev + 1);
                    pushTransientAlert(data, type);
                }
            });

            // Handle incoming 'renewal' events - calm auto-dismissing card, no forced acknowledgment
            socket.on('renewal', (data) => {
                if (data && data.id) {
                    setNotifications((prev) => [data, ...prev]);
                    setUnreadCount((prev) => prev + 1);
                    pushTransientAlert(data, 'Renewal');
                } else {
                    console.warn('Received malformed renewal event:', data);
                }
            });

            // Handle incoming 'new_lead' events
            socket.on('new_lead', (data) => {
                if (data && data.leadId) {
                    const type = data.type || 'Lead';
                    setNotifications((prev) => [data, ...prev]);
                    setUnreadCount((prev) => prev + 1);
                    pushAlert(data, type);
                } else {
                    console.warn('Received malformed new_lead event:', data);
                }
            });

            socket.on('disconnect', (reason) => {
                console.log('Socket.IO disconnected:', reason);
            });

            socket.on('connect_error', (err) => {
                console.error('Socket.IO connection error:', err.message);
            });

            socket.on('reconnect_attempt', (attempt) => {
                console.log(`Socket.IO reconnect attempt #${attempt}`);
            });

            socket.on('reconnect', (attempt) => {
                console.log(`Socket.IO reconnected on attempt #${attempt}`);
            });

            socket.on('reconnect_error', (err) => {
                console.error('Socket.IO reconnection error:', err.message);
            });

            return () => {
                if (socketRef.current) {
                    socketRef.current.disconnect();
                    socketRef.current = null;
                }
            };
        } else {
            if (socketRef.current) {
                socketRef.current.disconnect();
                socketRef.current = null;
            }

            setNotifications([]);
            setUnreadCount(0);
            setActiveAlerts([]);
            localStorage.removeItem(ACTIVE_ALERTS_STORAGE_KEY);
            Object.values(transientTimersRef.current).forEach(clearTimeout);
            transientTimersRef.current = {};
            setTransientAlerts([]);
        }
    }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

    // Function to mark a notification as read
    const markAsRead = async (id) => {
        try {
            const token = localStorage.getItem('accessToken');
            const roleid = localStorage.getItem('roleId');

            const response = await axios({
                method: 'post',
                headers: {
                    token: token,
                    roleid: roleid,
                },
                url: markNotificationAsRead(),
                data: { id },
            });

            setNotifications((prev) =>
                prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
            );
            setUnreadCount((prev) => Math.max(prev - 1, 0));
            showSnackbar(response.data?.message || 'Notification marked as read.', 'success');
        } catch (error) {
            console.error('Error marking notification as read:', error);
            showSnackbar('Failed to mark notification as read.', 'error');
        }
    };

    // Fires a fake event locally (no backend/socket involved) so the UI can be tested on demand.
    // Mirrors real behaviour: only 'new_lead' gets the persistent alarm + stack + forced
    // acknowledgment; 'followup'/'renewal' get a calm, auto-dismissing card shown at the
    // same top position.
    const triggerTestAlert = (type = 'new_lead') => {
        const sampleLeadId = Math.floor(1000 + Math.random() * 9000);

        if (type === 'new_lead') {
            pushAlert({ message: `New lead received - ID ${sampleLeadId} (test)`, leadId: sampleLeadId }, 'new_lead');
            return;
        }

        if (type === 'followup') {
            pushTransientAlert({ message: `Follow-up due - ID ${sampleLeadId} (test)`, leadId: sampleLeadId }, 'Lead');
            return;
        }

        pushTransientAlert({ message: `Policy renewal due - ID ${sampleLeadId} (test)`, leadId: sampleLeadId }, 'Renewal');
    };

    const value = {
        notifications,
        unreadCount,
        markAsRead,
        triggerTestAlert,
    };

    return (
        <NotificationsContext.Provider value={value}>
            {children}

            {/* Stacked, vibrant, persistent lead alert cards */}
            <Box
                sx={{
                    position: 'fixed',
                    top: 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 2000,
                    width: '100%',
                    maxWidth: 460,
                    px: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    pointerEvents: (activeAlerts.length || transientAlerts.length) ? 'auto' : 'none',
                }}
            >
                <TransitionGroup component={null}>
                    {activeAlerts.map((alert) => {
                        const gradient = typeGradientMapping[alert.type] || typeGradientMapping.Default;
                        const showView = alert.type === 'Opportunity' || alert.type === 'Lead' || alert.type === 'new_lead';

                        return (
                            <Collapse key={alert.uid}>
                                <Box
                                    sx={{
                                        position: 'relative',
                                        borderRadius: 2,
                                        p: 2,
                                        mb: 1.5,
                                        color: '#fff',
                                        backgroundImage: gradient,
                                        backgroundSize: '300% 300%',
                                        animation: `${gradientShift} 4s ease infinite, ${glowPulse} 1.8s ease-in-out infinite`,
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1.5,
                                    }}
                                >
                                    <NotificationsActiveIcon sx={{ mt: 0.3, animation: `${ring} 1s ease-in-out infinite` }} />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="subtitle2" fontWeight={700}>
                                            {alert.type === 'new_lead' || alert.type === 'Lead' ? 'New Lead' : alert.type}
                                        </Typography>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                            {alert.message}
                                        </Typography>
                                        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                                            {showView && (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    sx={{ bgcolor: 'rgba(255,255,255,0.25)', '&:hover': { bgcolor: 'rgba(255,255,255,0.4)' } }}
                                                    onClick={() => handleNavigateAlert(alert)}
                                                >
                                                    View
                                                </Button>
                                            )}
                                        </Box>
                                    </Box>
                                    <IconButton
                                        aria-label="acknowledge"
                                        size="small"
                                        onClick={() => acknowledgeAlert(alert.uid)}
                                        sx={{ color: '#fff' }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Collapse>
                        );
                    })}
                </TransitionGroup>

                {/* Calm, auto-dismissing cards for followup/renewal - same top position, lighter design */}
                <TransitionGroup component={null}>
                    {transientAlerts.map((alert) => {
                        const bgColor = transientColorMapping[alert.type] || transientColorMapping.Default;
                        const showView = alert.type === 'Opportunity' || alert.type === 'Lead';

                        return (
                            <Collapse key={alert.uid}>
                                <Box
                                    sx={{
                                        borderRadius: 2,
                                        p: 1.5,
                                        mb: 1.5,
                                        color: '#fff',
                                        bgcolor: bgColor,
                                        boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.25,
                                    }}
                                >
                                    <NotificationsNoneIcon fontSize="small" />
                                    <Typography variant="body2" sx={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
                                        {alert.message}
                                    </Typography>
                                    {showView && (
                                        <Button
                                            size="small"
                                            sx={{ color: '#fff', textDecoration: 'underline', minWidth: 'auto' }}
                                            onClick={() => handleNavigateTransient(alert)}
                                        >
                                            View
                                        </Button>
                                    )}
                                    <IconButton
                                        aria-label="dismiss"
                                        size="small"
                                        onClick={() => dismissTransientAlert(alert.uid)}
                                        sx={{ color: 'rgba(255,255,255,0.85)' }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Collapse>
                        );
                    })}
                </TransitionGroup>
            </Box>
        </NotificationsContext.Provider>
    );
};

// Custom hook for accessing notifications
export const useNotifications = () => useContext(NotificationsContext);
